import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Activity,
  RefreshCw,
  Wifi,
  WifiOff,
  AlertTriangle,
  Package,
  Clock,
  ArrowRight,
  Building2,
  CreditCard,
  Banknote,
  QrCode,
  Tag,
  Percent,
  Calendar,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import {
  useExecutiveSummary,
  useHourlySales,
  useTopSellingItems,
  useBranchPerformance,
  useExecutiveAlerts,
  useRecentOrders,
  usePaymentBreakdown,
  useCategorySales,
  useSalesByDay,
  usePromotionsSummary,
  useCancelledOrders,
  useLoyaltySummary,
  DatePreset,
  DateRangeParams,
} from '../../api/queries/executive.queries';
import { useBranches } from '../../api/queries/transfer.queries';
import { useAuthStore } from '../../stores/auth.store';
import MetricCard from './components/MetricCard';
import NLQChat from '../analytics/components/NLQChat';

// Payment method colors
const PAYMENT_COLORS: Record<string, string> = {
  cash: '#22c55e',
  card: '#3b82f6',
  qr: '#a855f7',
  promptpay: '#f97316',
  other: '#6b7280',
};

const PAYMENT_ICONS: Record<string, React.ElementType> = {
  cash: Banknote,
  card: CreditCard,
  qr: QrCode,
  promptpay: QrCode,
};

// Category colors for pie chart
const CATEGORY_COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ec4899', '#06b6d4'];

// Date preset options
const DATE_PRESETS: { value: DatePreset; label: string; labelKey: string }[] = [
  { value: 'today', label: 'Today', labelKey: 'reports.presets.today' },
  { value: 'yesterday', label: 'Yesterday', labelKey: 'reports.presets.yesterday' },
  { value: 'last7days', label: 'Last 7 Days', labelKey: 'reports.presets.last7days' },
  { value: 'last30days', label: 'Last 30 Days', labelKey: 'reports.presets.last30days' },
  { value: 'thisWeek', label: 'This Week', labelKey: 'reports.presets.thisWeek' },
  { value: 'lastWeek', label: 'Last Week', labelKey: 'reports.presets.lastWeek' },
  { value: 'thisMonth', label: 'This Month', labelKey: 'reports.presets.thisMonth' },
  { value: 'lastMonth', label: 'Last Month', labelKey: 'reports.presets.lastMonth' },
];

function ExecutiveDashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isHQ = user?.role === 'HQ_ADMIN';

  const [selectedBranch, setSelectedBranch] = useState<string>(isHQ ? 'all' : user?.branchId || '');
  const [datePreset, setDatePreset] = useState<DatePreset>('today');

  // Build date params
  const dateParams: DateRangeParams = useMemo(() => ({ preset: datePreset }), [datePreset]);

  // Fetch data with date params
  const { data: summary, refetch: refetchSummary, isFetching: isFetchingSummary } = useExecutiveSummary(selectedBranch, dateParams);
  const { data: hourlySales, refetch: refetchHourly } = useHourlySales(selectedBranch, dateParams);
  const { data: topItems, refetch: refetchTopItems } = useTopSellingItems(selectedBranch, 5, dateParams);
  const { data: branchPerformance, refetch: refetchBranches } = useBranchPerformance(dateParams);
  const { data: alerts, refetch: refetchAlerts } = useExecutiveAlerts(selectedBranch);
  const { data: recentOrders, refetch: refetchOrders, isFetching: isFetchingOrders } = useRecentOrders(selectedBranch);
  const { data: branches } = useBranches();

  // More data hooks with date params
  const { data: paymentBreakdown, refetch: refetchPayments } = usePaymentBreakdown(selectedBranch, dateParams);
  const { data: categorySales, refetch: refetchCategories } = useCategorySales(selectedBranch, 5, dateParams);
  const { data: salesByDay, refetch: refetchSalesByDay } = useSalesByDay(selectedBranch);
  const { data: promoSummary, refetch: refetchPromos } = usePromotionsSummary(selectedBranch, dateParams);
  const { data: cancelledOrders, refetch: refetchCancelled } = useCancelledOrders(selectedBranch, dateParams);
  const { data: loyaltySummary, refetch: refetchLoyalty } = useLoyaltySummary();

  const isConnected = !isFetchingSummary; // Simplified connection indicator
  const isToday = datePreset === 'today';

  // Get display label for current date preset
  const currentPresetLabel = DATE_PRESETS.find(p => p.value === datePreset)?.label || 'Today';

  const handleRefresh = useCallback(() => {
    refetchSummary();
    refetchHourly();
    refetchTopItems();
    if (isHQ) refetchBranches();
    refetchAlerts();
    refetchOrders();
    refetchPayments();
    refetchCategories();
    refetchSalesByDay();
    refetchPromos();
    refetchCancelled();
    refetchLoyalty();
  }, [refetchSummary, refetchHourly, refetchTopItems, refetchBranches, refetchAlerts, refetchOrders, isHQ, refetchPayments, refetchCategories, refetchSalesByDay, refetchPromos, refetchCancelled, refetchLoyalty]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `฿${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `฿${(amount / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatFullCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getItemName = (name: { en: string; th: string } | string) => {
    if (typeof name === 'string') return name;
    return i18n.language === 'th' ? name.th || name.en : name.en;
  };

  const getTimeAgo = (dateString?: string | null) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Handle future dates (shouldn't happen but just in case)
    if (diffMs < 0) return t('dashboard.justNow') || 'Just now';

    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return t('dashboard.justNow') || 'Just now';
    if (diffMins < 60) return `${diffMins} ${t('common.minutes') || 'min'} ago`;
    if (diffHours < 24) return `${diffHours} ${t('common.hours') || 'hr'} ago`;
    if (diffDays < 7) return `${diffDays} ${t('common.days') || 'days'} ago`;

    // For older dates, show actual date
    return date.toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-GB', {
      day: '2-digit',
      month: 'short',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
      case 'in_progress':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
      case 'ready':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
      case 'picked_up':
        return 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300';
      case 'completed':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-400';
      case 'cancelled':
        return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
      case 'voided':
        return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: t('orders.new') || 'New',
      in_progress: t('orders.inProgress') || 'Preparing',
      ready: t('orders.ready') || 'Ready',
      completed: t('orders.completed') || 'Completed',
      picked_up: t('kds.pickedUp') || 'Picked Up',
    };
    return labels[status] || status;
  };

  // Build reports URL with current filters
  const buildReportsUrl = (params: {
    tab?: string;
    paymentMethod?: string;
    category?: string;
    branchId?: string;
  }) => {
    const urlParams = new URLSearchParams();
    if (params.tab) urlParams.set('tab', params.tab);
    urlParams.set('preset', datePreset);
    if (selectedBranch && selectedBranch !== 'all') urlParams.set('branchId', selectedBranch);
    if (params.paymentMethod) urlParams.set('paymentMethod', params.paymentMethod);
    if (params.branchId) urlParams.set('branchId', params.branchId);
    return `/reports?${urlParams.toString()}`;
  };

  // Navigate to reports with specific filters
  const navigateToReports = (params: {
    tab?: string;
    paymentMethod?: string;
    category?: string;
    branchId?: string;
  }) => {
    navigate(buildReportsUrl(params));
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('dashboard.title') || 'Executive Dashboard'}</h1>
          <p className="text-muted-foreground text-sm">
            {t('dashboard.subtitle') || 'Real-time business overview'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              className="h-9 rounded-md border bg-background px-3 text-sm min-w-[140px]"
            >
              {DATE_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {t(preset.labelKey) || preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Branch Selector (HQ only) */}
          {isHQ && branches && (
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              <option value="all">{t('dashboard.allBranches') || 'All Branches'}</option>
              {branches.map((branch: any) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}

          {/* Connection Status */}
          <div className="flex items-center gap-1.5 text-sm">
            {isConnected ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-green-600 dark:text-green-400 hidden sm:inline">
                  {t('dashboard.live') || 'Live'}
                </span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-gray-400" />
                <span className="text-muted-foreground hidden sm:inline">
                  {t('dashboard.updating') || 'Updating...'}
                </span>
              </>
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isFetchingSummary}
            className="h-9 w-9 rounded-md border bg-background hover:bg-secondary flex items-center justify-center disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetchingSummary ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={isToday ? (t('dashboard.todayRevenue') || "Today's Revenue") : t('reports.revenue') || 'Revenue'}
          value={formatCurrency(summary?.today.revenue || 0)}
          rawValue={summary?.today.revenue || 0}
          icon={DollarSign}
          iconColor="text-green-600"
          comparison={
            isToday && summary?.yesterday.revenue
              ? { value: summary.yesterday.revenue, label: t('dashboard.vsYesterday') || 'vs yesterday' }
              : undefined
          }
        />
        <MetricCard
          title={isToday ? (t('dashboard.ordersToday') || 'Orders Today') : t('reports.orders') || 'Orders'}
          value={summary?.today.orders || 0}
          icon={ShoppingCart}
          iconColor="text-blue-600"
          comparison={
            isToday && summary?.yesterday.orders
              ? { value: summary.yesterday.orders, label: t('dashboard.vsYesterday') || 'vs yesterday' }
              : undefined
          }
        />
        <MetricCard
          title={t('dashboard.avgOrderValue') || 'Avg Order Value'}
          value={formatFullCurrency(summary?.today.averageOrderValue || 0)}
          rawValue={summary?.today.averageOrderValue || 0}
          icon={TrendingUp}
          iconColor="text-purple-600"
          comparison={
            isToday && summary?.yesterday.averageOrderValue
              ? { value: summary.yesterday.averageOrderValue, label: t('dashboard.vsYesterday') || 'vs yesterday' }
              : undefined
          }
        />
        <MetricCard
          title={t('dashboard.activeOrders') || 'Active Now'}
          value={summary?.today.activeOrders || 0}
          icon={Activity}
          iconColor="text-orange-600"
          subtitle={isToday ? (t('dashboard.inProgress') || 'orders in progress') : undefined}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hourly Sales Chart */}
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-4">
            {isToday ? (t('dashboard.salesTrend') || "Today's Sales Trend") : `${t('analytics.salesTrend') || 'Sales Trend'} (${t(`reports.presets.${datePreset}`) || currentPresetLabel})`}
          </h3>
          <div className="h-[200px]">
            {hourlySales?.data && hourlySales.data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={hourlySales.data}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="hour"
                    tickFormatter={(h) => `${h}:00`}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [formatFullCurrency(Number(value)), 'Revenue']}
                    labelFormatter={(h) => `${h}:00`}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData') || 'No data yet'}
              </div>
            )}
          </div>
        </div>

        {/* Top Selling Items */}
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-4">
            {isToday ? (t('dashboard.topSelling') || 'Top Selling Today') : `${t('analytics.topSellingItems') || 'Top Selling'} (${t(`reports.presets.${datePreset}`) || currentPresetLabel})`}
          </h3>
          <div className="space-y-3">
            {topItems && topItems.length > 0 ? (
              topItems.map((item, index) => (
                <div key={item.id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="flex-1 truncate text-sm">{getItemName(item.name)}</span>
                  <span className="text-sm font-medium">{item.quantity} sold</span>
                </div>
              ))
            ) : (
              <div className="h-[150px] flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData') || 'No data yet'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Breakdown & Category Sales Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payment Breakdown */}
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-semibold">{t('dashboard.paymentBreakdown') || 'Payment Methods'}</h3>
            </div>
            <Link to={buildReportsUrl({ tab: 'by-payment' })} className="text-xs text-primary hover:underline flex items-center gap-1">
              {t('dashboard.viewAll') || 'View all'}
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {paymentBreakdown && paymentBreakdown.breakdown.length > 0 ? (
            <div className="space-y-3">
              {paymentBreakdown.breakdown.map((payment) => {
                const Icon = PAYMENT_ICONS[payment.method] || CreditCard;
                const color = PAYMENT_COLORS[payment.method] || PAYMENT_COLORS.other;
                return (
                  <button
                    key={payment.method}
                    onClick={() => navigateToReports({ tab: 'orders', paymentMethod: payment.method })}
                    className="w-full flex items-center gap-3 p-2 -m-2 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                      <Icon className="h-4 w-4" style={{ color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize">{payment.method}</span>
                        <span className="text-sm text-muted-foreground">{payment.percentage}%</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${payment.percentage}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-semibold min-w-[80px] text-right">
                      {formatCurrency(payment.amount)}
                    </span>
                  </button>
                );
              })}
              <div className="pt-3 border-t flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('common.total')}</span>
                <span className="font-bold">{formatFullCurrency(paymentBreakdown.total.amount)}</span>
              </div>
            </div>
          ) : (
            <div className="h-[150px] flex items-center justify-center text-muted-foreground">
              {t('dashboard.noData') || 'No data yet'}
            </div>
          )}
        </div>

        {/* Category Sales */}
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-semibold">{t('dashboard.categorySales') || 'Top Categories'}</h3>
            </div>
            <Link to={buildReportsUrl({ tab: 'by-category' })} className="text-xs text-primary hover:underline flex items-center gap-1">
              {t('dashboard.viewAll') || 'View all'}
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {categorySales && categorySales.categories.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="w-[140px] h-[140px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categorySales.categories}
                      dataKey="revenue"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                    >
                      {categorySales.categories.map((_, index) => (
                        <Cell key={index} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {categorySales.categories.map((cat, index) => (
                  <Link
                    key={cat.id}
                    to={buildReportsUrl({ tab: 'by-category' })}
                    className="flex items-center gap-2 p-1 -m-1 rounded hover:bg-secondary/50 transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                    />
                    <span className="flex-1 text-sm truncate">{cat.name}</span>
                    <span className="text-sm font-medium">{formatCurrency(cat.revenue)}</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[150px] flex items-center justify-center text-muted-foreground">
              {t('dashboard.noData') || 'No data yet'}
            </div>
          )}
        </div>
      </div>

      {/* Sales by Day & Promotions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sales by Day of Week */}
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-4">{t('dashboard.salesByDay') || 'Sales by Day (4 Week Avg)'}</h3>
          <div className="h-[180px]">
            {salesByDay?.data && salesByDay.data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesByDay.data}>
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis
                    tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [formatFullCurrency(Number(value)), 'Average']}
                    labelFormatter={(day) => day}
                  />
                  <Bar dataKey="avgRevenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData') || 'No data yet'}
              </div>
            )}
          </div>
        </div>

        {/* Promotions & Discounts Summary */}
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 mb-4">
            <Percent className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold">{t('dashboard.promotions') || 'Promotions & Discounts'}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/promotions"
              className="bg-secondary/30 rounded-lg p-3 hover:bg-secondary/50 transition-colors"
            >
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatCurrency(promoSummary?.promotions.totalDiscount || 0)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('dashboard.promotionDiscount') || 'Promo Discount'}
              </div>
              <div className="text-xs text-muted-foreground">
                {promoSummary?.promotions.usageCount || 0} {t('dashboard.timesUsed') || 'times used'}
              </div>
            </Link>
            <Link
              to={buildReportsUrl({ tab: 'orders' })}
              className="bg-secondary/30 rounded-lg p-3 hover:bg-secondary/50 transition-colors"
            >
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {formatCurrency(promoSummary?.discounts.totalDiscount || 0)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('dashboard.totalDiscount') || 'Total Discount'}
              </div>
              <div className="text-xs text-muted-foreground">
                {promoSummary?.discounts.discountRate || 0}% {t('dashboard.ofOrders') || 'of orders'}
              </div>
            </Link>
            <Link
              to="/loyalty"
              className="bg-secondary/30 rounded-lg p-3 hover:bg-secondary/50 transition-colors"
            >
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {loyaltySummary?.totalMembers || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('dashboard.loyaltyMembers') || 'Loyalty Members'}
              </div>
              <div className="text-xs text-muted-foreground">
                +{loyaltySummary?.newMembersToday || 0} {t('common.today') || 'today'}
              </div>
            </Link>
            <Link
              to={`${buildReportsUrl({ tab: 'orders' })}&status=cancelled`}
              className="bg-secondary/30 rounded-lg p-3 hover:bg-secondary/50 transition-colors"
            >
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {cancelledOrders?.total.count || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('dashboard.cancelledOrders') || 'Cancelled/Void'}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(cancelledOrders?.total.value || 0)} {t('dashboard.lostValue') || 'lost'}
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Branch Performance & Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Branch Performance (HQ only) */}
        {isHQ && (
          <div className="bg-card rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">{t('dashboard.branchPerformance') || 'Branch Performance'}</h3>
              </div>
              <Link to={buildReportsUrl({ tab: 'by-branch' })} className="text-xs text-primary hover:underline flex items-center gap-1">
                {t('dashboard.viewAll') || 'View all'}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="h-[180px]">
              {branchPerformance && branchPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={branchPerformance}
                    layout="vertical"
                    onClick={(data: any) => {
                      if (data?.activePayload?.[0]?.payload?.id) {
                        navigateToReports({ tab: 'daily-sales', branchId: data.activePayload[0].payload.id });
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <XAxis type="number" tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={80}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(value) => [formatFullCurrency(Number(value)), 'Revenue']} />
                    <Bar dataKey="revenue" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  {t('dashboard.noData') || 'No data yet'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Alerts Panel */}
        <div className={`bg-card rounded-lg border p-4 ${!isHQ ? 'lg:col-span-2' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h3 className="font-semibold">{t('dashboard.needsAttention') || 'Needs Attention'}</h3>
            </div>
            {alerts && alerts.total > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 text-xs font-medium rounded-full">
                {alerts.total}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {alerts && alerts.lowStock > 0 && (
              <Link
                to="/inventory?status=low"
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-red-500" />
                  <span className="text-sm">
                    {alerts.lowStock} {t('dashboard.itemsLowStock') || 'items low on stock'}
                  </span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            )}
            {alerts && alerts.pendingPRs > 0 && (
              <Link
                to="/purchasing?tab=pr&status=pending"
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">
                    {alerts.pendingPRs} {t('dashboard.prsPending') || 'PRs pending approval'}
                  </span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            )}
            {alerts && alerts.pendingTransfers > 0 && (
              <Link
                to="/transfers?status=pending"
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">
                    {alerts.pendingTransfers} {t('dashboard.transfersPending') || 'transfers pending'}
                  </span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            )}
            {(!alerts || alerts.total === 0) && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                {t('dashboard.allClear') || 'All clear! No items need attention.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-card rounded-lg border p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-base">{t('dashboard.recentOrders') || 'Recent Orders'}</h3>
          <Link to="/orders" className="text-sm text-primary hover:underline flex items-center gap-1">
            {t('dashboard.viewAll') || 'View all'}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto -mx-5">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('orders.orderNumber') || 'Order'}
                </th>
                {isHQ && (
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('orders.branch') || 'Branch'}
                  </th>
                )}
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('orders.total') || 'Total'}
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('orders.items') || 'Items'}
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('orders.time') || 'Time'}
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('orders.status') || 'Status'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {recentOrders && recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-3.5">
                      <span className="font-semibold text-sm">#{order.orderNumber}</span>
                    </td>
                    {isHQ && (
                      <td className="px-5 py-3.5 text-sm text-muted-foreground">{order.branchName}</td>
                    )}
                    <td className="px-5 py-3.5 text-right">
                      <span className="font-medium text-sm">{formatFullCurrency(order.total)}</span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="text-sm text-muted-foreground">{order.itemCount}</span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <span className="text-sm text-muted-foreground">{getTimeAgo(order.createdAt)}</span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isHQ ? 6 : 5} className="px-5 py-10 text-center text-muted-foreground">
                    {isFetchingOrders ? t('common.loading') || 'Loading...' : t('dashboard.noOrders') || 'No recent orders'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* NLQ Chat - Ask Analytics */}
      <NLQChat />
    </div>
  );
}

export default ExecutiveDashboardPage;
